/* eslint-disable camelcase */
import { Error, RestaurantBranch, RestaurantOrg } from 'lib/types/global';
import { Org } from 'lib/types/session';
import {
  API_LOGIN_SUCCESS,
  API_READ_SESSION_SUCCESS,
} from '../session/actionTypes';
import { SessionAction } from '../session/actions';

export type BranchMap = Partial<Record<string, RestaurantBranch>>;
export type BranchesMap = Partial<Record<string, RestaurantBranch[]>>;
export type OrgMap = Partial<Record<string, RestaurantOrg>>;

export interface RestaurantsState {
  restaurants: [];
  summary: {};
  error?: Error;
  loading: boolean;
  branchesById: BranchMap;
  branchesByShortDrn: BranchMap;
  branchesByOrg: BranchesMap;
  branches: RestaurantBranch[];
  orgs: RestaurantOrg[];
  orgsById: OrgMap;
  orgsByShortDrn: OrgMap;
}

/* eslint-disable no-param-reassign */
export const initialState: RestaurantsState = {
  restaurants: [],
  summary: {}, // Ephemeral, restaurant and timerange specific.
  error: undefined,
  loading: false,

  branchesById: {},
  branchesByShortDrn: {},
  branchesByOrg: {},
  branches: [],

  orgs: [],
  orgsById: {},
  orgsByShortDrn: {},
};

type ReducerType = Pick<
  RestaurantsState,
  | 'branchesById'
  | 'branchesByOrg'
  | 'branches'
  | 'orgs'
  | 'orgsById'
  | 'orgsByShortDrn'
  | 'branchesByShortDrn'
>;

const formatRestaurants = (orgs: Org[]) =>
  orgs.reduce<ReducerType>(
    (result, item) => {
      const currentBranches = item.restaurants.filter(
        (branch) => branch.status !== 'DELETED',
      );
      const org: RestaurantOrg = {
        id: item.id,
        drnId: item.drn_id,
        shortDrn: item.short_drn,
        name: item.name,
        countryCode: item.country_code,
        languageCode: item.default_language_code,
        features: item.features,
        branches: currentBranches
          .filter((branch, _, branches) => {
            if (branches.length === 1) {
              return true;
            }

            return branch.is_active;
          })
          .map<RestaurantBranch>(
            ({
              customer_facing_link,
              fulfillment_types,
              hygiene_rating,
              hygiene_rating_last_updated,
              hygiene_rating_scheme,
              features,
              id,
              drn_id: drnId,
              short_drn: shortDrn,
              is_active,
              location,
              name,
              restaurant_type,
              rs_home_delivery_areas,
              rs_home_delivery_areas_v2,
              rs_home_delivery_areas_v2_radius,
              rs_home_delivery_areas_v3_3_multi_radius,
              time_zone,
              zone_id,
              zone_drn_id,
              zone_offset,
              customer_champion,
              branch_business_model,
              city,
              is_marketplace_plus_restaurant,
            }) => ({
              id,
              drnId,
              shortDrn,
              name,
              location,
              orgId: item.id,
              orgShortDrn: item.short_drn,
              orgDrnId: item.drn_id,
              zoneOffset: zone_offset,
              timeZone: time_zone,
              isActive: is_active,
              zoneId: zone_id,
              zoneDrnId: zone_drn_id,
              features,
              customerFacingLink: customer_facing_link,
              fulfillmentTypes: fulfillment_types,
              branchType: restaurant_type,
              hygieneRating: hygiene_rating,
              hygieneRatingLastUpdated: hygiene_rating_last_updated,
              hygieneRatingScheme: hygiene_rating_scheme,
              // eslint-disable-next-line camelcase
              isV1AreasEnabled: Boolean(rs_home_delivery_areas),
              // eslint-disable-next-line camelcase
              isV2AreasEnabled: Boolean(rs_home_delivery_areas_v2),
              // eslint-disable-next-line camelcase
              isV3AreasEnabled: Boolean(rs_home_delivery_areas_v2_radius),
              // eslint-disable-next-line camelcase
              isV3AreasMultiRadiusEnabled: Boolean(
                rs_home_delivery_areas_v3_3_multi_radius,
              ),
              // DELETE THIS FIELD FROM HERE + TYPES
              deliveryAreasEnabled: Boolean(
                // eslint-disable-next-line camelcase
                rs_home_delivery_areas ||
                  rs_home_delivery_areas_v2 ||
                  rs_home_delivery_areas_v2_radius ||
                  rs_home_delivery_areas_v3_3_multi_radius,
              ),
              isCustomerChampion: Boolean(customer_champion),
              branchBusinessModel: branch_business_model,
              city,
              isMarketplacePlusRestaurant: is_marketplace_plus_restaurant,
            }),
          ),
      };

      result.orgs.push(org);
      result.orgsById[org.id] = org;
      result.branchesByOrg[org.id] = [];
      result.branchesByOrg[org.shortDrn] = [];
      result.branchesByOrg[org.drnId] = [];
      result.orgsByShortDrn[org.shortDrn] = org;

      org.branches.forEach((branch) => {
        result.branches.push(branch);
        result.branchesById[branch.id] = branch;
        result.branchesByOrg[org.id]!.push(branch);
        result.branchesByOrg[org.shortDrn]!.push(branch);
        result.branchesByOrg[org.drnId]!.push(branch);
        result.branchesByShortDrn[branch.shortDrn] = branch;
      });
      return result;
    },
    {
      branchesById: {},
      branchesByOrg: {},
      branches: [],
      orgs: [],
      orgsById: {},
      orgsByShortDrn: {},
      branchesByShortDrn: {},
    },
  );

export default function restaurantsReducer(
  state = initialState,
  action: SessionAction,
) {
  switch (action.type) {
    case API_LOGIN_SUCCESS:
    case API_READ_SESSION_SUCCESS: {
      const { restaurant_companies: orgs } = action.payload;
      return {
        ...initialState,
        ...formatRestaurants(orgs),
      };
    }
    default:
      return state;
  }
}
